import { useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { ShieldCheck, MapPin, CaretDown, CaretUp, FileZip, Warning } from "@phosphor-icons/react";
import { downloadAuthFile } from "../../lib/fileOpen";
import HsPackList from "./HsPackList";

/** Operative portal home: every contract the operative is on, with its H&S pack and read receipts, available any time. */
export default function OperativeContractsCard() {
  const [contracts, setContracts] = useState(null);
  const [open, setOpen] = useState({});
  const load = () => API.get("/in-house-portal/contracts").then(({ data }) => setContracts(data.contracts || [])).catch(() => setContracts([]));
  useEffect(() => { load(); }, []);
  if (!contracts || !contracts.length) return null;

  const confirmRead = async (c, it) => {
    try {
      const { data: r } = await API.post(`/in-house-portal/contracts/${c.id}/hs-pack/${it.id}/read`);
      toast.success(r.remaining === 0 ? "All documents read — you're good to go" : `Confirmed · ${r.remaining} still to read`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not record that"); }
  };

  const totalUnread = contracts.reduce((n, c) => n + (c.unread || 0), 0);
  return (
    <section className="border border-border rounded-lg bg-card" data-testid="operative-contracts-card">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold font-[Chivo] flex items-center gap-1.5"><ShieldCheck size={15} className="text-emerald-700" /> My contracts &amp; H&amp;S packs</h2>
        <span className={`text-[11px] ${totalUnread ? "text-amber-700 font-semibold inline-flex items-center gap-1" : "text-muted-foreground"}`} data-testid="operative-contracts-unread">
          {totalUnread ? <><Warning size={12} weight="fill" /> {totalUnread} to read</> : `${contracts.length} contract${contracts.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <ul className="divide-y divide-border">
        {contracts.map(c => {
          const isOpen = open[c.id] ?? (contracts.length === 1 || c.unread > 0);
          return (
            <li key={c.id} data-testid={`operative-contract-${c.id}`}>
              <button type="button" onClick={() => setOpen(o => ({ ...o, [c.id]: !isOpen }))} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-muted/30" data-testid={`operative-contract-toggle-${c.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{c.title} <span className="text-muted-foreground font-normal text-xs">· {c.reference}</span></p>
                  <p className="text-[11px] text-muted-foreground truncate flex items-center gap-1"><MapPin size={11} /> {c.address || c.client_name}{c.schedule_text ? ` · ${c.schedule_text}` : ""}</p>
                </div>
                <span className={`text-[11px] whitespace-nowrap ${c.unread ? "text-amber-700 font-semibold" : "text-muted-foreground"}`}>{c.unread ? `${c.unread} to read` : `${c.hs_pack.length} doc${c.hs_pack.length === 1 ? "" : "s"}`}</span>
                {isOpen ? <CaretUp size={14} /> : <CaretDown size={14} />}
              </button>
              {isOpen && (
                <div className="px-4 pb-4 space-y-3">
                  {c.instructions && <p className="text-xs bg-muted/40 rounded-md p-2 whitespace-pre-line"><span className="text-muted-foreground">Team instructions:</span> {c.instructions}</p>}
                  {c.hs_pack.length > 0 && (
                    <p className={`text-xs rounded-md border px-3 py-2 flex items-center justify-between gap-2 ${c.unread ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}>
                      <span>{c.unread ? <><strong>{c.unread} to read</strong> — confirm each document before your first visit.</> : "Every document read and confirmed."}</span>
                      <button type="button" className="inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline" onClick={() => downloadAuthFile(`/in-house-portal/contracts/${c.id}/hs-pack.zip`, `${c.reference}_HS_pack.zip`)} data-testid={`operative-contract-zip-${c.id}`}><FileZip size={13} /> Download all</button>
                    </p>
                  )}
                  <HsPackList items={c.hs_pack} compact pdfUrl={(it) => `/in-house-portal/contracts/${c.id}/hs-pack/${it.id}/pdf`} onConfirmRead={(it) => confirmRead(c, it)} emptyText="No H&S documents have been added to this contract yet." testid={`operative-contract-pack-${c.id}`} />
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
